import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import Auth from "../../Auth";
import { useVatCheckoutStore } from "../../stores/vatCheckoutStore";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — sudokit Input.tsx exists; resolved at runtime by Rsbuild.
import { Input } from "../../sudokit/components/Input";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — sudokit primitives are JS without ambient types.
import { Spinner } from "../../sudokit/components/Spinner";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — sudokit primitives are JS without ambient types.
import { SudoButton } from "../../sudokit/components/SudoButton";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — sudokit primitives are JS without ambient types.
import { Switch } from "../../sudokit/components/Switch";
import {
  sendVatCheckoutError,
  sendVatClassificationSubmitted,
} from "./analytics";
import { getVatCountry } from "./vatCountries";
import {
  AffirmationLabel,
  AffirmationRow,
  HeaderSubtitle,
  LegalDisclaimer,
  ReminderText,
  SectionLabel,
  VatInlineError,
  VatLoadingContainer,
  VatStepContainer,
} from "./styles";

type Plan = {
  id: string;
  title?: string;
  interval: "month" | "year";
};

type VatClassification = "business" | "consumer";

type Props = {
  selectedPlan?: Plan | null;
  planInterval?: "month" | "year" | null;
  onComplete: () => void | Promise<void>;
  onBack?: () => void;
  loading?: boolean;
  userCountry: string | null;
  classification: VatClassification;
  origin?: string | null;
};

/**
 * Step 2 of the Upgrade Modal — VAT Information (SW-8538 Phase B).
 *
 * Callback-driven: on successful POST to `/api/vat-customer-update`, calls
 * `props.onComplete()`. The caller decides what happens next (e.g. Pricing
 * runs `subscription.changePlan(...)`, SubscribeButton hits Stripe's hosted
 * checkout). The component is now agnostic of the post-VAT path.
 *
 * Country + classification are passed in as props (resolved by the parent
 * via `useVatCheckoutGate()`). Form state (vatId, affirmation) lives in
 * `useVatCheckoutStore`.
 *
 * Checkbox visibility rule (flowchart):
 *   Rendered ONLY when `classification === "business" && no VAT ID entered`.
 *   B2C users never see it. B2B users who entered a VAT ID already earn
 *   reverse charge via the VAT ID, so the checkbox is redundant and hidden.
 *
 * Error handling:
 *   - `success: false` from the endpoint → render `error` message inline,
 *     do NOT call onComplete.
 *   - Network/throw → render generic message inline + Sentry.
 *   - Inline error is cleared on each new submit attempt.
 */
export default function VatCheckoutStep({
  selectedPlan,
  planInterval,
  onComplete,
  onBack,
  loading: parentLoading = false,
  userCountry,
  classification,
  origin,
}: Props) {
  const vatId = useVatCheckoutStore((state: any) => state.vatId);
  const setVatId = useVatCheckoutStore((state: any) => state.setVatId);
  const businessAffirmation = useVatCheckoutStore(
    (state: any) => state.businessAffirmation
  );
  const setBusinessAffirmation = useVatCheckoutStore(
    (state: any) => state.setBusinessAffirmation
  );
  const resetVatForm = useVatCheckoutStore((state: any) => state.resetVatForm);

  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    resetVatForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (vatId.trim() !== "") {
      setBusinessAffirmation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vatId]);

  // Loading guard — country not resolved yet. Hold the same approximate
  // height as the resolved form so the modal body doesn't jump when the
  // geo lookup completes.
  if (!userCountry) {
    return (
      <VatStepContainer>
        <VatLoadingContainer aria-live="polite">
          <Spinner size="md" label="Loading VAT information" />
        </VatLoadingContainer>
      </VatStepContainer>
    );
  }

  const countryData = getVatCountry(userCountry);

  // Defensive: if the parent somehow routes us to VatCheckoutStep for a
  // non-VAT country, fall back to a generic header.
  if (!countryData) {
    return (
      <VatStepContainer>
        <HeaderSubtitle>
          VAT information not available for your location.
        </HeaderSubtitle>
      </VatStepContainer>
    );
  }

  const showAffirmation =
    classification === "business" && vatId.trim() === "";

  const isLoading = submitting || parentLoading;

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setInlineError(null);

    const trimmedVatId = (vatId || "").trim();
    const hasVatId = trimmedVatId !== "";
    const affirmed = Boolean(businessAffirmation);

    const effectiveOutcome =
      hasVatId || (classification === "business" && affirmed)
        ? "reverse_charge"
        : "vat_applied";

    sendVatClassificationSubmitted({
      country: userCountry,
      classification,
      // The analytics wrapper expects a `source` string; we don't have access
      // to the survey source from inside the step, so leave a stable label.
      source: "vat_step",
      has_vat_id: hasVatId,
      affirmed_business: affirmed,
      effective_outcome: effectiveOutcome,
    });

    try {
      const result = await Auth.fetchWithIdToken("/api/vat-customer-update", {
        method: "POST",
        country: userCountry,
        vatId: hasVatId ? trimmedVatId : null,
        b2bAffirmation: affirmed,
        survey: classification,
      });

      if (!result || result.success !== true) {
        const message =
          (result && typeof result.error === "string" && result.error) ||
          "We couldn't save your VAT information. Please try again.";
        setInlineError(message);
        sendVatCheckoutError({
          country: userCountry,
          classification,
          error_type: "create_session",
          error_message: message,
        });
        return;
      }

      await onComplete();
    } catch (error) {
      const err = error as { message?: string } | null | undefined;
      setInlineError(
        "There was an error saving your VAT information. Please try again."
      );
      sendVatCheckoutError({
        country: userCountry,
        classification,
        error_type: "create_session",
        error_message: err?.message,
      });
      Sentry.captureException(error, {
        tags: { feature: "vat.checkout-step.submit" },
        extra: {
          country: userCountry,
          classification,
          origin: origin || null,
          planId: selectedPlan?.id,
          planInterval,
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Used so existing tests / analytics callers can pass `selectedPlan`
  // without TS complaints — kept as no-op references for now. `onBack` was
  // dropped from the rendered JSX but the prop is preserved so existing
  // callers (TaxModal) don't need to be refactored.
  void selectedPlan;
  void planInterval;
  void onBack;

  return (
    <VatStepContainer>
      <div>
        <h2 style={{ margin: "0 0 4px 0" }}>VAT Information</h2>
        <HeaderSubtitle>
          Based on your location in {countryData.name}, we may need to collect
          VAT. Your total will be shown on the next step.
        </HeaderSubtitle>
      </div>

      <div>
        <SectionLabel htmlFor="vat-id-input">
          VAT ID <span className="optional">(optional)</span>
        </SectionLabel>
        <Input
          id="vat-id-input"
          type="text"
          value={vatId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setVatId(e.target.value)
          }
          placeholder={countryData.idFormat}
        />
      </div>

      {showAffirmation && (
        <div>
          <AffirmationLabel>
            Or, don't have your VAT ID readily available?
          </AffirmationLabel>
          <AffirmationRow>
            <Switch
              checked={businessAffirmation}
              onCheckedChange={(checked: boolean) =>
                setBusinessAffirmation(Boolean(checked))
              }
              aria-label="I affirm that I use Sudowrite for business or professional purposes"
            />
            <span>
              I affirm that I use Sudowrite for business or professional
              purposes
            </span>
          </AffirmationRow>
          {businessAffirmation && (
            <>
              <ReminderText>
                You have 12 months to furnish your VAT ID - we'll remind you
                by email.
              </ReminderText>
              <LegalDisclaimer>
                Reverse charge – VAT to be accounted for by the customer
                under Article 196 of EU VAT Directive 2006/112/EC
              </LegalDisclaimer>
            </>
          )}
        </div>
      )}

      <SudoButton
        variant="primary"
        onClick={handleSubmit}
        disabled={isLoading}
      >
        {isLoading ? "Processing..." : "Continue to payment"}
      </SudoButton>

      {inlineError && (
        <VatInlineError role="alert">{inlineError}</VatInlineError>
      )}
    </VatStepContainer>
  );
}
